import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext.jsx';

/**
 * Gate a route subtree by auth + optional role list.
 *
 * <Route element={<ProtectedRoute roles={['hub_admin','inspector_general']} />}>
 *   …authenticated children…
 * </Route>
 */
export default function ProtectedRoute({ roles }) {
  const { isAuthenticated, hasRole, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoading />;

  if (!isAuthenticated) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  if (roles && roles.length > 0 && !hasRole(...roles)) {
    return <ForbiddenScreen />;
  }

  return <Outlet />;
}

function FullPageLoading() {
  return (
    <div className="grid min-h-screen place-items-center bg-white">
      <div className="flex items-center gap-3 text-sm text-slate-600">
        <span className="h-3 w-3 animate-pulse rounded-full bg-sky-500" aria-hidden />
        Verifying session…
      </div>
    </div>
  );
}

function ForbiddenScreen() {
  return (
    <div className="grid min-h-[60vh] place-items-center px-6">
      <div className="max-w-md text-center">
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-rose-600">Forbidden</div>
        <h1 className="mt-3 text-2xl font-semibold text-slate-900">Your role does not have access to this area.</h1>
        <p className="mt-3 text-sm text-slate-600">
          If you believe this is an error, contact your agency or institution administrator and have them confirm
          your role assignment in NACIS.
        </p>
      </div>
    </div>
  );
}
